/**
 * Support Diagnostics Model - Phase 14
 * 
 * Normalized diagnostics bundle shape for support/operations.
 * Aggregates data from offline queue, incidents, POS recovery, and system state.
 * 
 * DATA SOURCES MAPPED:
 * - /lib/offline/db.js: listQueueItems, listSyncLog, getQueueStats (local IndexedDB)
 * - /lib/offlineAudit/incidentStore.js: aggregateAllIncidents, getIncidentSummary (local)
 * - /lib/offline/network.js: getNetworkStatus, getEffectiveConnectionInfo (local)
 * - /lib/offline/posPolicies.js: POS offline action policies (local)
 * - /lib/offline/backofficePolicies.js: BackOffice offline policies (local)
 * - Browser APIs: navigator, localStorage (local)
 */

import { generateUUID } from '../offline/db';

// ==================== BUNDLE SHAPE ====================

/**
 * @typedef {Object} DiagnosticsBundle
 * @property {number} generatedAt
 * @property {Object} generatedBy
 * @property {Object} scope
 * @property {Object} appContext
 * @property {Object} terminalContext
 * @property {Object} queueSnapshot
 * @property {Object} replaySummary
 * @property {Object} incidentSummary
 * @property {Object} posRecoverySummary
 * @property {Object} networkDiagnostics
 * @property {Object} featureFlags
 * @property {Object} policySummary
 * @property {Object} sanitization
 * @property {Object} metadata
 */

/**
 * Create empty diagnostics bundle
 * @returns {DiagnosticsBundle}
 */
export function createEmptyBundle() {
  return {
    generatedAt: Date.now(),
    generatedBy: {
      userId: null,
      role: null
    },
    scope: {
      merchantId: null,
      locationId: null,
      terminalId: null,
      dateFrom: null,
      dateTo: null
    },
    appContext: {
      appVersion: null,
      buildVersion: null,
      pwaVersion: null,
      environment: null,
      timezone: null,
      browser: null,
      os: null,
      deviceType: null,
      lastAppStartAt: null,
      lastSuccessfulSyncAt: null
    },
    terminalContext: {
      terminalId: null,
      deviceId: null,
      locationId: null,
      cashierId: null,
      onlineStatus: null,
      lastHeartbeatAt: null
    },
    queueSnapshot: {
      pendingCount: 0,
      failedCount: 0,
      conflictCount: 0,
      blockedCount: 0,
      byDomain: {},
      oldestPendingAgeMs: null,
      newestPendingAgeMs: null
    },
    replaySummary: {
      successCount: 0,
      failureCount: 0,
      conflictCount: 0,
      retryCount: 0,
      recentEntries: []
    },
    incidentSummary: {
      openCount: 0,
      criticalCount: 0,
      blockedPaymentCount: 0,
      unresolvedIncidents: [],
      escalatedCount: 0
    },
    posRecoverySummary: {
      unsyncedOrders: 0,
      blockedPayments: 0,
      failedKitchen: 0,
      failedReceipts: 0,
      staleItems: 0
    },
    networkDiagnostics: {
      currentStatus: null,
      recentTransitions: [],
      backendReachable: null,
      lastHealthCheck: null,
      effectiveConnection: null
    },
    featureFlags: {
      offlineOrdersEnabled: null,
      offlineCashEnabled: null,
      strictOnlinePayments: null,
      offlineCardIntentQueueEnabled: null
    },
    policySummary: {
      paymentOfflinePolicy: null,
      notes: null
    },
    sanitization: {
      secretsRemoved: true,
      piiLevel: 'minimal',
      signedUrlsRemoved: true
    },
    metadata: {}
  };
}

// ==================== SUMMARY BUILDERS ====================

/**
 * Summarize queue snapshot from queue items
 * @param {Array} queueItems - Raw queue items from IndexedDB
 * @param {Object} scope - Filter scope
 * @returns {Object}
 */
export function summarizeQueueSnapshot(queueItems, scope = {}) {
  const now = Date.now();
  
  // Apply scope filters
  let filtered = queueItems;
  if (scope.merchantId) {
    filtered = filtered.filter(i => i.merchantId === scope.merchantId);
  }
  if (scope.locationId) {
    filtered = filtered.filter(i => i.locationId === scope.locationId);
  }
  if (scope.terminalId) {
    filtered = filtered.filter(i => i.terminalId === scope.terminalId);
  }
  if (scope.dateFrom) {
    filtered = filtered.filter(i => i.createdAt >= scope.dateFrom);
  }
  if (scope.dateTo) {
    filtered = filtered.filter(i => i.createdAt <= scope.dateTo);
  }
  
  const pending = filtered.filter(i => i.status === 'pending');
  const failed = filtered.filter(i => i.status === 'failed');
  const conflict = filtered.filter(i => i.status === 'conflict');
  const blocked = filtered.filter(i => i.blocked === true);
  
  // Group by domain/type
  const byDomain = {};
  for (const item of filtered) {
    const domain = mapTypeToDomain(item.type);
    byDomain[domain] = (byDomain[domain] || 0) + 1;
  }
  
  // Calculate age metrics
  let oldestPendingAgeMs = null;
  let newestPendingAgeMs = null;
  
  if (pending.length > 0) {
    const sorted = pending.sort((a, b) => a.createdAt - b.createdAt);
    oldestPendingAgeMs = now - sorted[0].createdAt;
    newestPendingAgeMs = now - sorted[sorted.length - 1].createdAt;
  }
  
  return {
    pendingCount: pending.length,
    failedCount: failed.length,
    conflictCount: conflict.length,
    blockedCount: blocked.length,
    byDomain,
    oldestPendingAgeMs,
    newestPendingAgeMs
  };
}

/**
 * Summarize replay history from sync logs
 * @param {Array} syncLogs - Raw sync log entries
 * @param {Object} scope - Filter scope
 * @returns {Object}
 */
export function summarizeReplayHistory(syncLogs, scope = {}) {
  // Apply date range filter
  let filtered = syncLogs;
  if (scope.dateFrom) {
    filtered = filtered.filter(l => l.syncedAt >= scope.dateFrom);
  }
  if (scope.dateTo) {
    filtered = filtered.filter(l => l.syncedAt <= scope.dateTo);
  }
  
  const success = filtered.filter(l => l.result === 'success');
  const failure = filtered.filter(l => l.result === 'failed');
  const conflict = filtered.filter(l => l.result === 'conflict');
  const retry = filtered.filter(l => l.result === 'retry');
  
  // Get recent entries (last 20)
  const recentEntries = filtered
    .sort((a, b) => b.syncedAt - a.syncedAt)
    .slice(0, 20)
    .map(entry => ({
      id: entry.id,
      queueItemId: entry.queueItemId,
      type: entry.type,
      result: entry.result,
      message: entry.message,
      syncedAt: entry.syncedAt,
      durationMs: entry.durationMs
    }));
  
  return {
    successCount: success.length,
    failureCount: failure.length,
    conflictCount: conflict.length,
    retryCount: retry.length,
    recentEntries
  };
}

/**
 * Summarize incidents for diagnostics
 * @param {Array} incidents - Normalized incidents
 * @param {Object} scope - Filter scope
 * @returns {Object}
 */
export function summarizeIncidents(incidents, scope = {}) {
  // Apply scope filters
  let filtered = incidents;
  if (scope.merchantId) {
    filtered = filtered.filter(i => i.merchantId === scope.merchantId);
  }
  if (scope.locationId) {
    filtered = filtered.filter(i => i.locationId === scope.locationId);
  }
  if (scope.terminalId) {
    filtered = filtered.filter(i => i.terminalId === scope.terminalId);
  }
  if (scope.dateFrom) {
    filtered = filtered.filter(i => i.occurredAt >= scope.dateFrom);
  }
  if (scope.dateTo) {
    filtered = filtered.filter(i => i.occurredAt <= scope.dateTo);
  }
  
  const open = filtered.filter(i => isIncidentOpen(i));
  const critical = filtered.filter(i => i.severity === 'critical');
  const blockedPayment = filtered.filter(i => 
    i.domain === 'payments' && (i.status === 'failed' || i.status === 'conflict')
  );
  const escalated = filtered.filter(i => i.followUpStatus === 'escalated');
  
  // Get unresolved incidents (limited to 50)
  const unresolvedIncidents = open
    .sort((a, b) => {
      // Sort by severity first
      const severityOrder = { critical: 0, error: 1, warning: 2, info: 3 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      return b.occurredAt - a.occurredAt;
    })
    .slice(0, 50)
    .map(incident => ({
      incidentId: incident.incidentId,
      title: incident.title,
      severity: incident.severity,
      status: incident.status,
      domain: incident.domain,
      occurredAt: incident.occurredAt,
      locationId: incident.locationId,
      terminalId: incident.terminalId
    }));
  
  return {
    openCount: open.length,
    criticalCount: critical.length,
    blockedPaymentCount: blockedPayment.length,
    unresolvedIncidents,
    escalatedCount: escalated.length
  };
}

/**
 * Summarize POS recovery state
 * @param {Array} queueItems - Queue items
 * @param {Array} incidents - Incidents
 * @param {Object} scope - Filter scope
 * @returns {Object}
 */
export function summarizePosRecovery(queueItems, incidents, scope = {}) {
  // Filter for POS-related items
  const posTypes = [
    'POS_ORDER_CREATE', 'POS_ORDER_UPDATE', 'POS_ORDER_CLOSE',
    'POS_PAYMENT_CASH', 'POS_PAYMENT_CARD',
    'POS_KITCHEN_SEND', 'POS_RECEIPT_PRINT'
  ];
  
  let posItems = queueItems.filter(i => posTypes.includes(i.type));
  let posIncidents = incidents.filter(i => i.sourceType === 'pos');
  
  // Apply scope filters
  if (scope.terminalId) {
    posItems = posItems.filter(i => i.terminalId === scope.terminalId);
    posIncidents = posIncidents.filter(i => i.terminalId === scope.terminalId);
  }
  if (scope.locationId) {
    posItems = posItems.filter(i => i.locationId === scope.locationId);
    posIncidents = posIncidents.filter(i => i.locationId === scope.locationId);
  }
  
  const unsyncedOrders = posItems.filter(i => 
    (i.type.includes('ORDER') && i.status !== 'completed')
  ).length;
  
  const blockedPayments = posItems.filter(i => 
    i.type.includes('PAYMENT') && (i.status === 'failed' || i.status === 'conflict')
  ).length;
  
  const failedKitchen = posItems.filter(i => 
    i.type === 'POS_KITCHEN_SEND' && i.status === 'failed'
  ).length;
  
  const failedReceipts = posItems.filter(i => 
    i.type === 'POS_RECEIPT_PRINT' && i.status === 'failed'
  ).length;
  
  // Stale items (older than 1 hour and still pending)
  const oneHour = 60 * 60 * 1000;
  const staleItems = posItems.filter(i => 
    i.status === 'pending' && (Date.now() - i.createdAt) > oneHour
  ).length;
  
  return {
    unsyncedOrders,
    blockedPayments,
    failedKitchen,
    failedReceipts,
    staleItems
  };
}

/**
 * Summarize network diagnostics
 * @param {Object} networkStatus - From network.js
 * @returns {Object}
 */
export function summarizeNetworkDiagnostics(networkStatus) {
  return {
    currentStatus: networkStatus?.online ? 'online' : 'offline',
    recentTransitions: [], // Would need event history tracking
    backendReachable: networkStatus?.heartbeatOk ?? null,
    lastHealthCheck: {
      checkedAt: networkStatus?.lastCheckedAt,
      success: networkStatus?.heartbeatOk
    },
    effectiveConnection: networkStatus?.effectiveType ? {
      type: networkStatus.effectiveType,
      downlink: networkStatus.downlink
    } : null
  };
}

// ==================== MAIN BUILDER ====================

/**
 * Build complete diagnostics bundle from input sources
 * @param {Object} inputSources
 * @returns {DiagnosticsBundle}
 */
export function buildDiagnosticsBundle(inputSources) {
  const {
    scope = {},
    user = {},
    queueItems = [],
    syncLogs = [],
    incidents = [],
    networkStatus = {},
    featureFlags = {},
    policies = {}
  } = inputSources;
  
  const bundle = createEmptyBundle();
  
  // Set metadata
  bundle.generatedAt = Date.now();
  bundle.generatedBy = {
    userId: user.userId || null,
    role: user.role || null
  };
  
  // Set scope
  bundle.scope = {
    merchantId: scope.merchantId || null,
    locationId: scope.locationId || null,
    terminalId: scope.terminalId || null,
    dateFrom: scope.dateFrom || null,
    dateTo: scope.dateTo || null
  };
  
  // App context
  bundle.appContext = buildAppContext();
  
  // Terminal context
  bundle.terminalContext = buildTerminalContext(scope);
  
  // Queue snapshot
  bundle.queueSnapshot = summarizeQueueSnapshot(queueItems, scope);
  
  // Replay summary
  bundle.replaySummary = summarizeReplayHistory(syncLogs, scope);
  
  // Incident summary
  bundle.incidentSummary = summarizeIncidents(incidents, scope);
  
  // POS recovery summary
  bundle.posRecoverySummary = summarizePosRecovery(queueItems, incidents, scope);
  
  // Network diagnostics
  bundle.networkDiagnostics = summarizeNetworkDiagnostics(networkStatus);
  
  // Feature flags
  bundle.featureFlags = {
    offlineOrdersEnabled: featureFlags.offlineOrdersEnabled ?? null,
    offlineCashEnabled: featureFlags.offlineCashEnabled ?? null,
    strictOnlinePayments: featureFlags.strictOnlinePayments ?? null,
    offlineCardIntentQueueEnabled: featureFlags.offlineCardIntentQueueEnabled ?? null
  };
  
  // Policy summary
  bundle.policySummary = {
    paymentOfflinePolicy: policies.paymentOfflinePolicy || null,
    notes: policies.notes || null
  };
  
  return bundle;
}

// ==================== HELPERS ====================

/**
 * Build app context from browser environment
 * @returns {Object}
 */
function buildAppContext() {
  const ctx = {
    appVersion: null,
    buildVersion: null,
    pwaVersion: null,
    environment: null,
    timezone: null,
    browser: null,
    os: null,
    deviceType: null,
    lastAppStartAt: null,
    lastSuccessfulSyncAt: null
  };
  
  if (typeof window !== 'undefined') {
    // App version from meta tag or env
    ctx.appVersion = process.env.REACT_APP_VERSION || 
                     document.querySelector('meta[name="app-version"]')?.content || 
                     '1.0.0';
    ctx.buildVersion = process.env.REACT_APP_BUILD_VERSION || null;
    ctx.environment = process.env.NODE_ENV || 'production';
    
    // Timezone
    ctx.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Browser info
    const ua = navigator.userAgent;
    ctx.browser = detectBrowser(ua);
    ctx.os = detectOS(ua);
    ctx.deviceType = detectDeviceType(ua);
    
    // PWA version from service worker
    if (navigator.serviceWorker?.controller) {
      ctx.pwaVersion = 'active';
    }
    
    // Last sync from localStorage
    const lastSync = localStorage.getItem('asterpos_last_successful_sync');
    ctx.lastSuccessfulSyncAt = lastSync ? parseInt(lastSync, 10) : null;
  }
  
  return ctx;
}

/**
 * Build terminal context
 * @param {Object} scope
 * @returns {Object}
 */
function buildTerminalContext(scope) {
  const ctx = {
    terminalId: scope.terminalId || null,
    deviceId: null,
    locationId: scope.locationId || null,
    cashierId: null,
    onlineStatus: null,
    lastHeartbeatAt: null
  };
  
  if (typeof localStorage !== 'undefined') {
    ctx.deviceId = localStorage.getItem('asterpos_device_id') || null;
    ctx.cashierId = localStorage.getItem('asterpos_current_cashier') || null;
  }
  
  return ctx;
}

/**
 * Map queue item type to domain
 */
function mapTypeToDomain(type) {
  const mapping = {
    'TIMECLOCK_PUNCH': 'timeclock',
    'AVAILABILITY_UPDATE': 'availability',
    'SCHEDULE_ACK': 'schedule',
    'POS_ORDER_CREATE': 'orders',
    'POS_ORDER_UPDATE': 'orders',
    'POS_ORDER_CLOSE': 'orders',
    'POS_PAYMENT_CASH': 'payments',
    'POS_PAYMENT_CARD': 'payments',
    'POS_KITCHEN_SEND': 'kitchen',
    'POS_RECEIPT_PRINT': 'receipts'
  };
  return mapping[type] || 'other';
}

/**
 * Check if incident is open
 */
function isIncidentOpen(incident) {
  const closedStatuses = ['resolved', 'dismissed'];
  return !closedStatuses.includes(incident.status);
}

/**
 * Detect browser from user agent
 */
function detectBrowser(ua) {
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

/**
 * Detect OS from user agent
 */
function detectOS(ua) {
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

/**
 * Detect device type from user agent
 */
function detectDeviceType(ua) {
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  if (/mobile|iphone|android/i.test(ua)) return 'mobile';
  return 'desktop';
}

export default {
  createEmptyBundle,
  buildDiagnosticsBundle,
  summarizeQueueSnapshot,
  summarizeReplayHistory,
  summarizeIncidents,
  summarizePosRecovery,
  summarizeNetworkDiagnostics
};
